import { apiService } from './api';
import { Organization, CreateOrganizationRequest, ApiResponse, OrganizationRestaurant, CreateOrganizationRestaurantRequest, Employee, AppRole } from '../types';

export const organizationService = {
  async getOrganizations(): Promise<Organization[]> {
    const response = await apiService.get<Organization[]>('/organizations');
    return response.data;
  },

  async getOrganizationByCode(code: string): Promise<Organization> {
    const response = await apiService.get<Organization>(`/organizations/by-code/${code}`);
    return response.data;
  },

  async getOrganizationById(id: string): Promise<Organization> {
    const response = await apiService.get<Organization>(`/organizations/${id}`);
    return response.data;
  },

  async createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
    const response = await apiService.post<Organization>('/organizations', data);
    return response.data;
  },

  async updateOrganization(id: string, data: Partial<CreateOrganizationRequest>): Promise<Organization> {
    const response = await apiService.put<Organization>(`/organizations/${id}`, data);
    return response.data;
  },

  async toggleOrganizationStatus(id: string, active: boolean): Promise<ApiResponse<null>> {
    const response = await apiService.patch<ApiResponse<null>>(`/organizations/${id}/active`, { active });
    return response.data;
  },

  async deleteOrganization(id: string): Promise<ApiResponse<null>> {
    const response = await apiService.delete<ApiResponse<null>>(`/organizations/${id}`);
    return response.data;
  },

  async hardDeleteOrganization(id: string): Promise<ApiResponse<null>> {
    const response = await apiService.delete<ApiResponse<null>>(`/organizations/${id}/hard`);
    return response.data;
  },

  // Restaurants under an Organization
  async getRestaurantsByOrganization(organizationId: string): Promise<OrganizationRestaurant[]> {
    const response = await apiService.get<OrganizationRestaurant[]>(`/organizations/${organizationId}/restaurants`);
    return response.data;
  },

  async createRestaurant(organizationId: string, data: CreateOrganizationRestaurantRequest): Promise<OrganizationRestaurant> {
    const response = await apiService.post<OrganizationRestaurant>(`/organizations/${organizationId}/restaurants`, data);
    return response.data;
  },

  async updateRestaurant(organizationId: string, restaurantId: string, data: Partial<CreateOrganizationRestaurantRequest>): Promise<OrganizationRestaurant> {
    const response = await apiService.put<OrganizationRestaurant>(`/organizations/${organizationId}/restaurants/${restaurantId}`, data);
    return response.data;
  },

  async deleteRestaurant(organizationId: string, restaurantId: string): Promise<void> {
    await apiService.delete(`/organizations/${organizationId}/restaurants/${restaurantId}`);
  },

  async toggleRestaurantStatus(organizationId: string, restaurantId: string): Promise<OrganizationRestaurant> {
    const response = await apiService.patch<OrganizationRestaurant>(`/organizations/${organizationId}/restaurants/${restaurantId}/status`);
    return response.data;
  },

  async getRestaurantEmployees(organizationId: string, restaurantId: string): Promise<Employee[]> {
    const response = await apiService.get<Employee[]>(`/organizations/${organizationId}/restaurants/${restaurantId}/employees`);
    return response.data;
  },

  // Org-specific roles
  async getRolesByOrgCode(orgCode: string): Promise<AppRole[]> {
    const response = await apiService.get<AppRole[]>(`/roles/org/${orgCode}`);
    return response.data;
  },

  async createRole(orgCode: string, role: Omit<AppRole, 'id' | 'organizationCode'>): Promise<AppRole> {
    const response = await apiService.post<AppRole>(`/roles/org/${orgCode}`, role);
    return response.data;
  },

  async updateRole(id: string, role: Omit<AppRole, 'id' | 'organizationCode'>): Promise<AppRole> {
    const response = await apiService.put<AppRole>(`/roles/${id}`, role);
    return response.data;
  },

  async deleteRole(id: string): Promise<void> {
    await apiService.delete(`/roles/${id}`);
  },
};